import React, { useMemo, useState } from 'react';
import {
  getLighthouseElementPreview,
  lighthouseSnippetMeta,
} from '../utils/auditDictionary';

function cardTitle(cs, files) {
  const meta = lighthouseSnippetMeta(cs.snippet);
  if (meta) return meta;
  const preview = getLighthouseElementPreview(cs, files);
  if (preview.mode === 'text' || preview.mode === 'html') {
    const t = preview.text || '';
    return t.length > 80 ? `${t.slice(0, 80)}…` : t;
  }
  return 'Élément de la page';
}

function ElementImagePreview({ imageUrl, alt }) {
  const [failed, setFailed] = useState(false);

  if (!imageUrl) {
    return (
      <p className="text-xs text-blue-gray-500 mb-2">
        URL de l&apos;image non retrouvée dans le rapport Lighthouse.
      </p>
    );
  }

  return (
    <div className="mb-2 rounded-lg border border-blue-gray-200 bg-blue-gray-50 overflow-hidden">
      {!failed ? (
        <a
          href={imageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
          title="Ouvrir l’image en taille réelle"
        >
          <img
            src={imageUrl}
            alt={alt || 'Aperçu de l’élément'}
            className="w-full max-h-48 min-h-[80px] object-contain object-center bg-white"
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={() => setFailed(true)}
          />
        </a>
      ) : (
        <div className="px-3 py-4 text-center bg-white">
          <p className="text-xs text-blue-gray-500 mb-2">
            L&apos;aperçu intégré est bloqué par le serveur d&apos;images.
          </p>
          <a
            href={imageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium text-accent hover:underline"
          >
            Ouvrir l&apos;image dans un nouvel onglet
          </a>
        </div>
      )}
    </div>
  );
}

function ElementPreview({ cs, files }) {
  const preview = getLighthouseElementPreview(cs, files);
  if (preview.mode === 'empty') return null;

  return (
    <div className="px-3 py-2.5 bg-white">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-gray-400 mb-1.5">
        Aperçu sur la page
      </p>
      {preview.mode === 'image' && (
        <>
          <ElementImagePreview
            imageUrl={preview.imageUrl}
            alt={lighthouseSnippetMeta(cs.snippet) || preview.label || 'Image concernée'}
          />
          {preview.tag && (
            <details className="mt-1">
              <summary className="text-[10px] text-blue-gray-400 cursor-pointer hover:text-blue-gray-600 select-none">
                Balise HTML
              </summary>
              <code className="block text-[10px] font-mono text-blue-gray-500 bg-blue-gray-50 border border-blue-gray-200 rounded-md px-2 py-1.5 break-all max-h-20 overflow-auto mt-1">
                {preview.tag}
              </code>
            </details>
          )}
        </>
      )}
      {preview.mode === 'html' && (
        <div
          className="text-sm text-blue-gray-800 bg-blue-gray-50 border border-blue-gray-200 rounded-md px-3 py-2 max-h-28 overflow-auto break-words [&_*]:max-w-full"
          dangerouslySetInnerHTML={{ __html: preview.html }}
        />
      )}
      {preview.mode === 'text' && (
        <div className="bg-blue-gray-50 border border-blue-gray-200 rounded-md px-3 py-2.5">
          <p className="text-sm text-blue-gray-900 leading-relaxed whitespace-pre-wrap break-words">
            {preview.text}
          </p>
          {preview.tag && (
            <p className="text-[10px] font-mono text-blue-gray-400 mt-2 pt-2 border-t border-blue-gray-200/80 break-all">
              {preview.tag}
            </p>
          )}
        </div>
      )}
      {preview.mode === 'tag-only' && (
        <code className="block text-xs font-mono text-blue-gray-600 bg-blue-gray-50 border border-blue-gray-200 rounded-md px-3 py-2 break-all">
          {preview.tag}
        </code>
      )}
    </div>
  );
}

/**
 * Aperçu des éléments concernés par une opportunité Lighthouse (snippets + sélecteur).
 * @param {{ detailsInfo?: { codeSnippets?: any[], files?: any[], warnings?: any[], sizes?: any[] } }} props
 */
export default function LighthouseAffectedElements({ detailsInfo, maxItems = 5 }) {
  if (!detailsInfo) return null;

  const snippets = detailsInfo.codeSnippets || [];
  const files = useMemo(() => {
    const base = [...(detailsInfo.files || [])];
    const seen = new Set(base.map((f) => f.url).filter(Boolean));
    for (const url of detailsInfo.urls || []) {
      if (typeof url === 'string' && url && !seen.has(url)) {
        seen.add(url);
        base.push({
          url,
          name: url.split('/').pop()?.split('?')[0] || url,
        });
      }
    }
    return base;
  }, [detailsInfo.files, detailsInfo.urls]);
  const hasSnippets = snippets.length > 0;
  const hasFiles = files.length > 0;

  if (!hasSnippets && !hasFiles) return null;

  return (
    <div className="space-y-3">
      {hasSnippets && (
        <div>
          <p className="text-xs font-semibold text-blue-gray-600 mb-2">
            Éléments concernés ({snippets.length})
          </p>
          <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar">
            {snippets.slice(0, maxItems).map((cs, ci) => (
                <div
                  key={ci}
                  className="rounded-lg border border-blue-gray-200 bg-white overflow-hidden"
                >
                  <div className="px-3 py-2 border-b border-blue-gray-100 bg-blue-gray-50/60">
                    <p className="text-sm font-medium text-blue-gray-800 leading-snug">
                      {cardTitle(cs, files)}
                    </p>
                    {cs.explanation && (
                      <p className="text-xs text-amber-700/90 mt-0.5">{cs.explanation}</p>
                    )}
                  </div>
                  <ElementPreview cs={cs} files={files} />
                  {cs.selector && (
                    <details className="group border-t border-blue-gray-100 bg-blue-gray-50/40">
                      <summary className="px-3 py-1.5 text-[10px] text-blue-gray-400 cursor-pointer hover:text-blue-gray-600 select-none">
                        Détail technique (sélecteur CSS)
                      </summary>
                      <code className="block px-3 pb-2 text-[10px] font-mono text-blue-gray-500 break-all leading-relaxed">
                        {cs.selector}
                      </code>
                    </details>
                  )}
                </div>
            ))}
            {snippets.length > maxItems && (
              <p className="text-xs text-blue-gray-400 text-center py-1">
                + {snippets.length - maxItems} autre{snippets.length - maxItems > 1 ? 's' : ''} élément{snippets.length - maxItems > 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      )}

      {hasFiles && (
        <div>
          <p className="text-xs font-semibold text-blue-gray-600 mb-1.5">
            Fichiers concernés ({files.length})
          </p>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {files.slice(0, 8).map((f, fi) => (
              <div
                key={fi}
                className="flex items-center justify-between gap-2 bg-blue-gray-50 rounded-lg px-3 py-2 text-xs"
              >
                <a
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline truncate font-mono flex-1 min-w-0"
                >
                  {f.name || f.url?.split('/').pop()?.split('?')[0] || f.url}
                </a>
                {f.size != null && f.size > 0 && (
                  <span className="text-blue-gray-400 flex-shrink-0">
                    {f.size > 1024 * 1024
                      ? `${(f.size / 1024 / 1024).toFixed(1)} MB`
                      : f.size > 1024
                        ? `${(f.size / 1024).toFixed(0)} KB`
                        : `${f.size} B`}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
